import React, { useEffect, useState } from 'react';
import { serviceLinksApi } from '../services/api';
import './OrgStaticKeysPanel.css';

const getErrorMessage = (err, fallback) => (
  err?.response?.data?.error?.message
  || err?.response?.data?.message
  || err?.response?.data?.error
  || err?.message
  || fallback
);

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const STALE_THRESHOLD_MS = 10 * 60 * 1000;

const getServiceTestId = (value) => String(value || 'unknown').toLowerCase().replace(/[^a-z0-9]+/g, '-');

function getConnectionState(lastSeenAt) {
  if (!lastSeenAt) return 'never';
  const elapsed = Date.now() - new Date(lastSeenAt).getTime();
  if (Number.isNaN(elapsed)) return 'never';
  return elapsed < STALE_THRESHOLD_MS ? 'connected' : 'stale';
}

function ConnectionStateIndicator({ state, testId }) {
  const labels = {
    connected: 'Connected',
    stale: 'Stale',
    never: 'Never connected',
  };
  return (
    <span className={`conn-indicator conn-${state}`} title={labels[state]} data-testid={testId}>
      <span className="conn-dot" />
      {labels[state]}
    </span>
  );
}

const OrgStaticKeysPanel = ({ testId = 'org-static-keys' }) => {
  const [installations, setInstallations] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadContext = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await serviceLinksApi.getContext();
      const payload = response.data?.data || response.data || {};
      setInstallations(Array.isArray(payload.installations) ? payload.installations : []);
      setServices(Array.isArray(payload.services) ? payload.services : []);
    } catch (err) {
      console.error('Failed to load organisation service links:', err);
      const status = err?.response?.status;
      if ([401, 403].includes(status)) {
        setError('Elevated administrator session required to view organisation service links.');
      } else {
        setError(getErrorMessage(err, 'Failed to load organisation service links.'));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContext();
  }, []);

  const groups = installations.reduce((acc, installation) => {
    const appCode = installation.appCode || 'unknown';
    if (!acc[appCode]) {
      const service = services.find((entry) => entry.appCode === appCode);
      acc[appCode] = {
        label: installation.serviceLabel || service?.label || appCode,
        installations: [],
      };
    }
    acc[appCode].installations.push(installation);
    return acc;
  }, {});

  const hasInstallations = installations.length > 0;

  return (
    <section className="org-static-keys-panel" data-testid={testId}>
      <div className="org-key-header">
        <div>
          <h3>Organisation Service Keys</h3>
          <p>
            Every linked service for organisations you administer, including active human logins.
            Service keys remain read-only and secret material is never shown.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline"
          onClick={loadContext}
          disabled={loading}
          data-testid={`${testId}-refresh`}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error ? <div className="message error" data-testid={`${testId}-error`}>{error}</div> : null}

      {loading && !hasInstallations ? (
        <p className="org-key-hint" data-testid={`${testId}-loading`}>Loading…</p>
      ) : !hasInstallations ? (
        <p className="org-key-hint" data-testid={`${testId}-empty`}>No linked services for your organisations.</p>
      ) : (
        <div className="org-key-groups" data-testid={`${testId}-list`}>
          {Object.entries(groups)
            .sort(([, a], [, b]) => a.label.localeCompare(b.label))
            .map(([appCode, group]) => {
              const { installations: serviceInstallations } = group;
              const connectedCount = serviceInstallations
                .map((installation) => getConnectionState(installation.lastSeenAt))
                .filter((state) => state === 'connected').length;
              const activeSessionCount = serviceInstallations.reduce(
                (total, installation) => total + (installation.loggedInUsers?.length || 0),
                0,
              );
              const serviceTestId = getServiceTestId(appCode);
              return (
                <div key={appCode} className="org-key-group" data-testid={`${testId}-service-${serviceTestId}`}>
                  <div className="org-key-group-header">
                    <h4 data-testid={`${testId}-group-${serviceTestId}`}>{group.label}</h4>
                    <span className="org-key-group-count" data-testid={`${testId}-count-${serviceTestId}`}>
                      {connectedCount}/{serviceInstallations.length} connected · {activeSessionCount} active {activeSessionCount === 1 ? 'login' : 'logins'}
                    </span>
                  </div>
                  <div className="org-key-list">
                    {serviceInstallations.map((installation) => {
                      const keyObject = installation.serviceKey;
                      const keyActive = Boolean(keyObject?.present);
                      const connectionState = getConnectionState(installation.lastSeenAt);
                      const loggedInUsers = installation.loggedInUsers || [];
                      return (
                        <article
                          key={installation.installationUuid}
                          className={`org-key-card ${installation.status === 'active' ? 'is-active' : 'is-inactive'}`}
                          data-testid={`${testId}-card`}
                          data-installation-uuid={installation.installationUuid}
                        >
                          <div className="org-key-card-main">
                            <div className="org-key-card-title-row">
                              <strong data-testid={`${testId}-label`}>
                                {installation.installLabel || installation.hostname || installation.installationUuid}
                              </strong>
                              <ConnectionStateIndicator state={connectionState} testId={`${testId}-connection-state`} />
                            </div>
                            <span>Org: {installation.organisationName || installation.ownerOrgId || 'Unassigned'}</span>
                            <span className="org-key-installation-id" data-testid={`${testId}-installation-uuid`}>
                              Installation: {installation.installationUuid}
                            </span>
                            {installation.hostname ? <span>Host: {installation.hostname}</span> : null}
                            {installation.lastSeenIp ? (
                              <span className="org-key-ip" data-testid={`${testId}-ip`}>
                                Service IP: {installation.lastSeenIp}
                              </span>
                            ) : null}
                            <span>
                              Status: <span className={`org-key-status ${installation.status === 'active' ? 'active' : 'inactive'}`} data-testid={`${testId}-status`}>{installation.status}</span>
                            </span>
                          </div>
                          <div className="org-key-meta">
                            <dl className="org-key-dates">
                              <div>
                                <dt>Linked</dt>
                                <dd>{formatDateTime(installation.linkedAt)}</dd>
                              </div>
                              <div>
                                <dt>Last rotated</dt>
                                <dd>{formatDateTime(installation.serviceKeyRotatedAt)}</dd>
                              </div>
                              <div>
                                <dt>Last seen</dt>
                                <dd>{formatDateTime(installation.lastSeenAt)}</dd>
                              </div>
                            </dl>
                            {keyObject ? (
                              <span className="org-key-fingerprint" data-testid={`${testId}-key-state`}>
                                {keyObject.environment ? `${keyObject.environment} · ` : ''}{keyActive ? 'Active' : 'Revoked'}
                              </span>
                            ) : installation.serviceAuthMode === 'client_credentials' ? (
                              <span className="org-key-fingerprint" data-testid={`${testId}-key-state`}>Client credentials configured</span>
                            ) : (
                              <span className="org-key-fingerprint" data-testid={`${testId}-key-state`}>No key</span>
                            )}
                          </div>

                          <section className="org-service-logins" aria-label={`Active logins for ${installation.installLabel || installation.installationUuid}`}>
                            <h5>Active service logins</h5>
                            {loggedInUsers.length === 0 ? (
                              <p data-testid={`${testId}-logins-empty`}>No users currently logged in.</p>
                            ) : (
                              <ul data-testid={`${testId}-logins`}>
                                {loggedInUsers.map((session) => (
                                  <li key={session.sessionUuid} data-testid={`${testId}-login-user`}>
                                    <div>
                                      <strong>{session.displayName}</strong>
                                      <span className="org-service-login-user-id">User: {session.userUuid}</span>
                                    </div>
                                    <div className="org-service-login-meta">
                                      <span>{session.nodeObservedIp ? `Node-observed user IP: ${session.nodeObservedIp}` : (session.coreObservedNodeIp ? `Core-observed IP: ${session.coreObservedNodeIp}` : 'User IP unavailable')}</span>
                                      {session.safeDeviceSummary ? <span>{session.safeDeviceSummary}</span> : null}
                                      <span>Last active: {formatDateTime(session.lastSeenAt)}</span>
                                      <span>Expires: {formatDateTime(session.expiresAt)}</span>
                                      {session.rememberMe ? <span>Remembered login</span> : null}
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </section>
                        </article>
                      );
                    })}
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </section>
  );
};

export default OrgStaticKeysPanel;
